import { useEffect, useImperativeHandle, useRef, type CSSProperties, type ReactNode, type Ref } from 'react';
import {
  animate,
  motion,
  useMotionValue,
  usePresence,
  useReducedMotion,
  useTransform,
  type AnimationPlaybackControls,
} from 'motion/react';
import { curves, durations, scaledDuration } from '../theme/motion';
import { isDragIgnored, isInsideScrolledScroller } from './scrollUtils';
import { useVerticalDrag } from './useVerticalDrag';
import styles from './SwipeDismissLayer.module.css';

type Ease = readonly [number, number, number, number];

/** easeOutCubic's starting slope (progress per unit of time): y1 / x1 of its first control point. */
const OUT_CUBIC_START_SLOPE = curves.easeOutCubic[1] / curves.easeOutCubic[0];

/**
 * A release that carries the finger's speed (polish pass): `distance` and
 * `speed` in the same unit (fraction of the travel, per second for speed),
 * `speed` measured towards the target. The duration is what easeOutCubic
 * would need to start at that speed, kept within [minMs, maxMs], and the curve
 * is an ease-out whose starting slope matches the finger, so a flick never
 * visibly slows (or jumps ahead) the moment it is let go. Null when the
 * finger was not moving towards the target (the caller keeps its scaled
 * duration).
 */
export function flingSettle(distance: number, speed: number, minMs: number, maxMs: number): { ms: number; ease: Ease } | null {
  if (!(distance > 0) || !(speed > 0)) return null;
  const ms = Math.min(maxMs, Math.max(minMs, ((OUT_CUBIC_START_SLOPE * distance) / speed) * 1000));
  // Starting slope in progress per unit time: the finger's speed over the average speed.
  const slope = Math.min(8, Math.max(1, (speed * ms) / 1000 / distance));
  const x1 = Math.min(curves.easeOutCubic[0], 0.95 / slope);
  return { ms, ease: [x1, x1 * slope, curves.easeOutCubic[2], 1] };
}

export interface SwipeDismissLayerHandle {
  /** Slides the layer out (240 ms easeInCubic, the route pop) and then calls onDismiss. */
  dismiss(): void;
}

export interface SwipeDismissLayerProps {
  children: ReactNode;
  /** Called once the layer has slid off-screen (navigate away here). */
  onDismiss: () => void;
  /** Fraction of the viewport height past which a release dismisses (default 0.28; player 0.24). */
  dismissThreshold?: number;
  /** Downward release velocity (px/s) that dismisses (default 1200; player 1100). */
  minFlingVelocity?: number;
  dismissDuration?: number;
  settleDuration?: number;
  /** Dim layer opacity at rest (default 0.5; the player uses 0 = no dim). */
  maxDimOpacity?: number;
  dismissOnBackgroundTap?: boolean;
  dragStartSlop?: number;
  dragSensitivity?: number;
  scrollTopTolerance?: number;
  /** Return false to refuse a drag that starts on `target`. */
  canStartDismiss?: (target: EventTarget | null) => boolean;
  /** Slide in from the bottom on mount (SwipeDismissPageRoute push, 320 ms easeOutCubic). */
  animateIn?: boolean;
  ref?: Ref<SwipeDismissLayerHandle>;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  role?: string;
}

/**
 * SwipeDismissibleSheet + SwipeDismissPageRoute (spec 01 §10.4, 07 §3.3):
 * a full-screen layer the user drags down to dismiss. Works with inner scroll
 * views (only starts when they are at the top). Inside <AnimatePresence> it
 * also plays the 240 ms pop when removed.
 */
export function SwipeDismissLayer({
  children,
  onDismiss,
  dismissThreshold = 0.28,
  minFlingVelocity = 1200,
  dismissDuration = durations.swipeDismiss,
  settleDuration = durations.swipeSettle,
  maxDimOpacity = 0.5,
  dismissOnBackgroundTap = true,
  dragStartSlop = 6,
  dragSensitivity = 1,
  scrollTopTolerance = 0.5,
  canStartDismiss,
  animateIn = true,
  ref,
  className,
  style,
  'aria-label': ariaLabel,
  role,
}: SwipeDismissLayerProps) {
  const [isPresent, safeToRemove] = usePresence();
  const reduceMotion = useReducedMotion() ?? false;
  // Two controllers, as in Flutter: the route (push/pop slide, moves the whole
  // page including the dim layer) and the sheet drag (moves the content). A
  // touch during the push-in therefore never interrupts the slide.
  const route = useMotionValue(animateIn ? 1 : 0);
  const drag = useMotionValue(0);
  const routeY = useTransform(route, (r) => `${r * 100}%`);
  const dragY = useTransform(drag, (p) => `${p * 100}%`);
  const dim = useTransform(drag, (p) => maxDimOpacity * (1 - p));
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const routeControls = useRef<AnimationPlaybackControls | null>(null);
  const dragControls = useRef<AnimationPlaybackControls | null>(null);
  const dismissing = useRef(false);
  const dismissedRef = useRef(onDismiss);
  dismissedRef.current = onDismiss;

  const seconds = (ms: number) => (reduceMotion ? 0 : ms / 1000);

  const runRoute = (target: number, ms: number, ease: Ease, onComplete?: () => void) => {
    routeControls.current?.stop();
    routeControls.current = animate(route, target, { duration: seconds(ms), ease, onComplete });
  };

  const runDrag = (target: number, ms: number, ease: Ease, onComplete?: () => void) => {
    dragControls.current?.stop();
    dragControls.current = animate(drag, target, { duration: seconds(ms), ease, onComplete });
  };

  /**
   * Sheet dismissal (drag release or dim tap): the content slides off, then
   * onDismiss. `speed` is the release velocity (fraction of the travel per
   * second, downward); without one the duration scales with the distance left.
   */
  const dismissByDrag = (speed = 0) => {
    if (dismissing.current) return;
    dismissing.current = true;
    const remaining = 1 - drag.get();
    const fling = flingSettle(remaining, speed, durations.swipeMin, dismissDuration);
    const ms = fling?.ms ?? scaledDuration(dismissDuration, remaining);
    runDrag(1, ms, fling?.ease ?? curves.easeOutCubic, () => dismissedRef.current());
  };

  /** Snap back to rest; an upward release carries its speed into the settle. */
  const settleBack = (speed = 0) => {
    const p = drag.get();
    const fling = flingSettle(p, speed, durations.swipeMin, settleDuration);
    runDrag(0, fling?.ms ?? scaledDuration(settleDuration, p), fling?.ease ?? curves.easeOutCubic);
  };

  useImperativeHandle(ref, () => ({
    // Navigator.pop: the route slides out (240 ms easeInCubic), then onDismiss.
    dismiss: () => {
      if (dismissing.current) return;
      dismissing.current = true;
      dragControls.current?.stop();
      runRoute(1, durations.routePop * (1 - route.get()), curves.easeInCubic, () => dismissedRef.current());
    },
  }));

  useEffect(() => {
    if (isPresent) {
      // Back on the stack while leaving (Back, then Forward at once): it can be dragged and dismissed again.
      const returning = dismissing.current;
      dismissing.current = false;
      if (animateIn || returning) runRoute(0, durations.routePush * (returning ? route.get() : 1), curves.easeOutCubic);
    } else {
      dismissing.current = true;
      dragControls.current?.stop();
      // Already off-screen (dragged away or popped): remove at once.
      const remaining = 1 - route.get() - drag.get();
      if (remaining <= 0.001) safeToRemove?.();
      else runRoute(1, durations.routePop * (1 - route.get()), curves.easeInCubic, () => safeToRemove?.());
    }
  }, [isPresent]);

  useEffect(
    () => () => {
      routeControls.current?.stop();
      dragControls.current?.stop();
    },
    [],
  );

  const viewportHeight = () => (rootRef.current?.clientHeight || window.innerHeight || 1) * dragSensitivity;

  useVerticalDrag(
    contentRef,
    {
      onDown: () => {
        if (!dismissing.current) dragControls.current?.stop();
      },
      skipSlop: () => drag.get() > 0,
      shouldStart: (dy, dx, target) => {
        const root = contentRef.current;
        if (!root || dismissing.current) return false;
        if (canStartDismiss && !canStartDismiss(target)) return false;
        if (drag.get() > 0) return true;
        if (dy <= 0 || Math.abs(dy) < Math.abs(dx)) return false;
        return !isInsideScrolledScroller(target, root, scrollTopTolerance) && !isDragIgnored(target, root);
      },
      onMove: (delta) => {
        drag.set(Math.min(1, Math.max(0, drag.get() + delta / viewportHeight())));
      },
      onEnd: (velocity) => {
        const p = drag.get();
        // px/s of the finger → fraction of the travel per second (the content moves 1/sensitivity per finger px).
        const speed = velocity / viewportHeight();
        if (p >= dismissThreshold || velocity >= minFlingVelocity) dismissByDrag(speed);
        else settleBack(-speed);
      },
      onRelease: () => {
        if (drag.get() > 0 && !dismissing.current) settleBack();
      },
    },
    { slop: dragStartSlop },
  );

  return (
    <div ref={rootRef} className={styles.root}>
      <motion.div className={styles.route} style={{ y: routeY }}>
        {maxDimOpacity > 0 && (
          <motion.div className={styles.dim} style={{ opacity: dim }} onClick={dismissOnBackgroundTap ? () => dismissByDrag() : undefined} aria-hidden />
        )}
        <motion.div
          ref={contentRef}
          className={[styles.content, className ?? ''].filter(Boolean).join(' ')}
          style={{ ...style, y: dragY }}
          role={role}
          aria-label={ariaLabel}
        >
          {children}
        </motion.div>
      </motion.div>
    </div>
  );
}
